import { useCallback, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Box, useTheme } from "@mui/material";
import Button from "components/basic/button";
import ScrollContainer from "components/basic/scrollContainer";
import { ChargerEstimatorData } from "pages/chargerEstimator";
import ChargerCard, { ChargerCardHandle } from "../../chargerCard";
import { QuoteServiceSource } from "../../../quote/quoteTools";

interface QuoteCarouselProps {
  services: QuoteServiceSource[];
  chargerConfigData: ChargerEstimatorData;
  activeIndex: number;
  onActiveIndexChange: (index: number) => void;
  /** Opens the quote for whichever card is currently shown. */
  onExpand: () => void;
  /** Filled with each card's handle so the page can expand the visible one. */
  cardRefs: React.MutableRefObject<(ChargerCardHandle | null)[]>;
}

/**
 * One quote at a time on small screens, swiped horizontally. Paging is CSS
 * scroll-snap rather than a gesture library, so it keeps native momentum and
 * rubber-banding on iOS for free. The card fills the screen and scrolls; the
 * dots and Expand sit in a footer pinned below it.
 */
const QuoteCarousel: React.FC<QuoteCarouselProps> = ({
  services,
  chargerConfigData,
  activeIndex,
  onActiveIndexChange,
  onExpand,
  cardRefs,
}) => {
  const theme = useTheme();
  const { t } = useTranslation("estimator");
  const trackRef = useRef<HTMLDivElement>(null);

  const handleScroll = useCallback(() => {
    const track = trackRef.current;
    if (!track) {
      return;
    }

    const index = Math.round(track.scrollLeft / track.clientWidth);
    const clamped = Math.min(Math.max(index, 0), services.length - 1);

    if (clamped !== activeIndex) {
      onActiveIndexChange(clamped);
    }
  }, [activeIndex, onActiveIndexChange, services.length]);

  // Jumps rather than animates: a smooth programmatic scroll gets cancelled by
  // the container's mandatory snapping, leaving the dots pointing at a card the
  // user never moved to. Swipes keep their native momentum either way.
  const scrollToIndex = (index: number) => {
    const track = trackRef.current;
    if (!track) {
      return;
    }

    onActiveIndexChange(index);
    track.scrollTo({ left: index * track.clientWidth });
  };

  // A sort change reorders the list under the user, so return to the first card
  // instead of leaving them on whatever now sits at the old offset. Keyed on the
  // list itself, never on activeIndex — doing that mid-scroll snaps the track
  // back to 0 under the user's finger the moment they swipe near the start.
  const serviceOrder = services.map((service) => service.id).join(",");
  useEffect(() => {
    trackRef.current?.scrollTo({ left: 0 });
  }, [serviceOrder]);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        flex: 1,
        minHeight: 0,
        // Definite height, so the track below can shrink instead of overflowing.
        height: "100%",
        width: "100%",
      }}
    >
      <Box
        ref={trackRef}
        onScroll={handleScroll}
        sx={{
          display: "flex",
          flex: 1,
          minHeight: 0,
          overflowX: "auto",
          overflowY: "hidden",
          scrollSnapType: "x mandatory",
          // The dots are the affordance; a scrollbar under the card is noise.
          scrollbarWidth: "none",
          "&::-webkit-scrollbar": { display: "none" },
        }}
      >
        {services.map((service, index) => (
          <Box
            key={service.id}
            sx={{
              flex: "0 0 100%",
              minWidth: 0,
              scrollSnapAlign: "center",
              scrollSnapStop: "always",
              display: "flex",
              justifyContent: "center",
              boxSizing: "border-box",
            }}
          >
            <ScrollContainer>
              <ChargerCard
                ref={(handle) => {
                  cardRefs.current[index] = handle;
                }}
                service={service}
                chargerConfigData={chargerConfigData}
                allServices={services}
                serviceIndex={index}
                showExpandAction={false}
              />
            </ScrollContainer>
          </Box>
        ))}
      </Box>

      <Box
        sx={{
          flexShrink: 0,
          backgroundColor: theme.palette.primary.main,
          display: "flex",
          flexDirection: "column",
          alignItems: "stretch",
          gap: 1,
          paddingX: 2,
          paddingTop: 1.5,
          // Keeps the button clear of the iOS home indicator.
          paddingBottom: `calc(${theme.spacing(1.5)} + env(safe-area-inset-bottom))`,
        }}
      >
        {services.length > 1 && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              gap: 1,
            }}
          >
            {services.map((service, index) => (
              <Box
                key={service.id}
                component="button"
                type="button"
                aria-label={t("select.show_quote", {
                  index: index + 1,
                  total: services.length,
                })}
                aria-current={index === activeIndex}
                onClick={() => scrollToIndex(index)}
                sx={{
                  // Padding keeps the tap target at 24px while the dot stays 8px.
                  padding: "8px",
                  border: "none",
                  background: "none",
                  cursor: "pointer",
                  lineHeight: 0,
                  "&::after": {
                    content: '""',
                    display: "block",
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    transition: "background-color 0.2s ease",
                    backgroundColor:
                      index === activeIndex
                        ? theme.palette.warning.main
                        : theme.palette.neutralAlpha.white45,
                  },
                }}
              />
            ))}
          </Box>
        )}

        <Button
          style={{
            color: theme.palette.common.white,
            fontSize: "1.4em",
            fontWeight: "bolder",
          }}
          variant="contained"
          fullWidth
          color="warning"
          onClick={onExpand}
          size="large"
        >
          {t("select.expand")}
        </Button>
      </Box>
    </Box>
  );
};

export default QuoteCarousel;
